import { Modal, Spin, Progress, Tag, Button } from "antd";

/**
 * AnalyticsProgressModal - Specialized modal for analytics processing
 * props:
 *  - processing: bool (analytics processing phase)
 *  - activeStep: number (1..totalSteps)
 *  - totalSteps: number (defaults to 4)
 *  - onClose: fn
 *  - disableClose: bool
 *  - modalSelection: string (selected model name)
 *  - dependentVariable: string (selected dependent variable)
 *  - groupBy: array (selected group by columns)
 *  - MODEL_OPTIONS: array (available model options)
 *  - onCancel: fn (cancel handler)
 */
const AnalyticsProgressModal = ({
  processing = false,
  activeStep: activeStepProp,
  totalSteps = 4,
  onClose,
  disableClose = true,
  modalSelection,
  dependentVariable,
  groupBy = [],
  MODEL_OPTIONS = [],
  onCancel,
}) => {
  const visible = !!processing;

  // derive active step: prefer prop, else 1 for processing start
  let activeStep = typeof activeStepProp === "number" ? activeStepProp : 1;

  const percent = processing
    ? Math.round((Math.min(Math.max(activeStep, 1), totalSteps) / totalSteps) * 100)
    : 0;

  const handleCancel = () => {
    if (disableClose) return;
    if (typeof onClose === "function") onClose();
  };

  // analytics-specific step messages
  const stepMessages = [
    "Initializing Analytics Engine",
    "Processing Selected Model Configuration",
    "Applying Data Transformations",
    "Generating Analytics Results",
  ];

  // Get model display name
  const getModelName = () => {
    const model = MODEL_OPTIONS?.find((opt) => opt?.value === modalSelection);
    return model?.label || String(modalSelection || 'Unknown Model');
  };

  return (
    <Modal
      title={<div className="text-white">Analytics Processing</div>}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      destroyOnHidden
      centered
      styles={{
        content: {
          backgroundColor: "#1f2937", // gray-800
          border: "1px solid #374151", // gray-700
        },
        header: {
          backgroundColor: "#1f2937", // gray-800
        },
        mask: {
          backdropFilter: "blur(5px)",
          WebkitBackdropFilter: "blur(5px)",
          backgroundColor: "rgba(15, 23, 42, 0.6)",
        },
      }}
      maskClosable={!disableClose}
      closable={!disableClose}
      width={600}
    >
      <div style={{ textAlign: "center" }}>
        {processing && (
          <>
            <div style={{ marginBottom: 12 }}>
              <Spin tip={<span className="text-gray-300">Processing analytics...</span>} />
            </div>

            <Progress
              percent={percent}
              status={activeStep < totalSteps ? "active" : "normal"}
              style={{ marginBottom: 12 }}
              trailColor="#374151"
              format={(percent) => <span className="text-gray-300">{percent}%</span>}
            />

            <div style={{ marginBottom: 12, color: "#d1d5db" }}>
              Step {Math.min(activeStep, totalSteps)} of {totalSteps}
            </div>


            {/* Dynamic Configuration Display */}
            <div style={{
              marginTop: 16,
              padding: 16,
              borderRadius: 8,
              background: "rgba(255,255,255,0.05)",
              textAlign: "left"
            }}>
              <div className="text-white font-bold text-base mb-3 block">
                Executing Selected Configuration
              </div>
              
              <div style={{ marginBottom: 8 }}>
                <span className="text-white font-bold">✨ Model Chosen:</span>
                <span className="text-gray-300 ml-2 font-medium">
                  {getModelName()}
                </span>
              </div>
              
              <div style={{ marginBottom: 8 }}>
                <span className="text-white font-bold">🧙 Applying DBSCAN sorcery on :</span>
                <span className="text-gray-300 ml-2 font-medium">
                  {dependentVariable || '-'}
                </span>
              </div>
              
              <div>
                <span className="text-white font-bold">🔮 Clustering Columns:</span>
                <div style={{ marginTop: 4 }}>
                  {Array.isArray(groupBy) && groupBy.length > 0 ? (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {groupBy.map((g) => (
                        <Tag key={g} color="blue" style={{ borderRadius: 12, padding: "2px 8px" }}>
                          {g}
                        </Tag>
                      ))}
                    </div>
                  ) : (
                    <span className="text-gray-500">None</span>
                  )}
                </div>
              </div>
            </div>

            {onCancel && (
              <div style={{ marginTop: 24 }}>
                <Button danger onClick={onCancel}>Cancel Processing</Button>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default AnalyticsProgressModal;
